import React, { useCallback, useEffect, useState, useMemo } from "react"
import { useDropzone } from "react-dropzone"
import { FormattedMessage, useIntl } from "gatsby-plugin-intl"
import { useFormikContext } from "formik"
import { Button, Spinner } from "@tmu/components/common"
import { v4 as uuidv4 } from "uuid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-light-svg-icons/faPlus"
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash"
import { faCrop } from "@fortawesome/pro-solid-svg-icons/faCrop"
import {
  StyledImagesContainerList,
  StyledDropzoneContent,
  StyledImageControls,
  StyledImageThumb,
  StyledSpinner,
  StyledErrorText,
} from "./index.styles"
import { readFile } from "@tmu/utils/image"
import ImageCropper from "./ImageCropperNew"
import { useToast } from "@tmu/hooks"
import { Tooltip } from "react-tooltip"

const Dropzone = ({ onDrop, accept, isEmpty, children }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: 3145728,
  })

  const handleClick = (e) => {
    e.preventDefault()
    const input = document.getElementById("dropzoneInput")
    if (input) {
      input.value = "" // Reset input value
      input.click()
    }
  }

  return (
    <div {...getRootProps()} onClick={(e) => e.preventDefault()}>
      <input
        id="dropzoneInput"
        className="dropzone-input"
        {...getInputProps()}
      />
      <StyledDropzoneContent active={isDragActive}>
        {!isEmpty ? (
          children
        ) : isDragActive ? (
          <FormattedMessage
            id="dashboard::forms::releaseFiles"
            defaultMessage="Release to drop the files here"
            tagName="p"
          />
        ) : (
          <>
            <div className="open-dialog" onClick={handleClick}>
              <FontAwesomeIcon icon={faPlus} size="3x" />
            </div>
            <FormattedMessage
              id="dashboard::forms::dragFiles"
              defaultMessage="Drag 'n' drop some files here, or click to select files"
              tagName="p"
            />
          </>
        )}
      </StyledDropzoneContent>
    </div>
  )
}

const ImageThumbnail = React.memo(
  ({ image, loading, id, onRemove, onEdit }) => {
    console.log("ImageThumbnail component props:", { image, loading, id })

    // Add validation for required props
    if (!image || !id) {
      console.warn("ImageThumbnail component received invalid props:", {
        image,
        id,
      })
      return null
    }

    const [imgSrc, setImgSrc] = useState(null)
    const [hasError, setHasError] = useState(false)
    const [hasShownCrop, setHasShownCrop] = useState(false)
    const isUserUpload = id?.indexOf("user-upload") > -1
    const isExistingImage =
      typeof image === "string" && !image.startsWith("data:")
    const { formatMessage } = useIntl()

    useEffect(() => {
      console.log("ImageThumbnail useEffect - image changed:", {
        image,
        isUserUpload,
        isExistingImage,
      })

      // For existing images, use the URL directly
      if (isExistingImage) {
        setImgSrc(image)
        return
      }

      // For new uploads
      if (!isUserUpload || !image) {
        return
      }

      // If image is already a data URL, use it directly
      if (typeof image === "string" && image.startsWith("data:")) {
        setImgSrc(image)
        return
      }

      // If image is a File object, read it
      if (image instanceof File || image instanceof Blob) {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result === "string") {
            setImgSrc(reader.result)
          }
        }
        reader.onerror = (error) => {
          console.error("Error reading file:", error)
          setImgSrc(null)
        }
        reader.readAsDataURL(image)
      }
    }, [image, isUserUpload, isExistingImage])

    // Show crop popup when image is first loaded (only for new uploads)
    useEffect(() => {
      if (imgSrc && isUserUpload && !hasShownCrop && !isExistingImage) {
        console.log("Auto-showing crop popup for new image")
        setHasShownCrop(true)
        onEdit(id)
      }
    }, [imgSrc, isUserUpload, hasShownCrop, id, onEdit, isExistingImage])

    const checkImageRatio = useCallback((src) => {
      if (!src) return

      const img = new window.Image()
      img.onload = () => {
        const ratio = img.width / img.height
        // Check if ratio is approximately 16:9 (allowing for small variations)
        const isCorrectRatio = Math.abs(ratio - 16 / 9) < 0.1
        setHasError(!isCorrectRatio)
      }
      img.onerror = () => {
        console.error("Error loading image:", src)
        setHasError(true)
      }
      img.src = src
    }, [])

    useEffect(() => {
      if (imgSrc) {
        checkImageRatio(imgSrc)
      }
    }, [imgSrc, checkImageRatio])

    if (!imgSrc) {
      return (
        <StyledImageThumb>
          <StyledSpinner>
            <Spinner condensed />
          </StyledSpinner>
        </StyledImageThumb>
      )
    }

    return (
      <>
        <StyledImageThumb hasError={hasError}>
          <img width={196} height={118} alt={`img-${id}`} src={imgSrc} />
          <div>
            {loading ? (
              <StyledSpinner>
                <Spinner condensed />
              </StyledSpinner>
            ) : (
              <>
                {isUserUpload && (
                  <>
                    <div>
                      {hasError && (
                        <StyledErrorText>
                          <FormattedMessage
                            id="dashboard::forms::incorrectImageRatio"
                            defaultMessage="Image ratio should be 16:9"
                          />
                        </StyledErrorText>
                      )}
                    </div>
                  </>
                )}
                <StyledImageControls>
                  {isUserUpload && (
                    <>
                      <div>
                        <Button
                          variant="text"
                          label="crop"
                          data-tooltip-id="repeat"
                          onClick={(e) => {
                            e.preventDefault()
                            console.log("Manual crop button clicked")
                            onEdit(id)
                          }}>
                          <FontAwesomeIcon icon={faCrop} />
                        </Button>
                      </div>
                    </>
                  )}
                  <Button
                    variant="text"
                    label="trash"
                    data-tooltip-content={formatMessage({
                      id: "dashboard::forms::delete",
                      defaultMessage: "Delete",
                    })}
                    data-tooltip-id="remove"
                    onClick={(e) => {
                      e.preventDefault()
                      onRemove(id)
                    }}>
                    <Tooltip
                      place="left"
                      type="info"
                      id="remove"
                      textColor="black"
                      backgroundColor="white"
                      className="tooltip"
                      delayShow={200}
                    />
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </StyledImageControls>
              </>
            )}
          </div>
        </StyledImageThumb>
      </>
    )
  }
)

const ImageList = React.memo(
  ({ images = [], processing = [], onRemove, onEdit }) => {
    console.log("ImageList props:", { images, processing })

    const renderImage = useCallback(
      (item) => {
        console.log("Rendering image item:", item)

        if (!item || typeof item !== "object") {
          console.warn("Invalid image item:", item)
          return null
        }

        if (!item.image || !item.id) {
          console.warn("Image item missing required properties:", item)
          return null
        }

        return (
          <ImageThumbnail
            image={item.image}
            key={`${item.id}-image`}
            id={item.id}
            loading={processing.indexOf(item.id) > -1}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        )
      },
      [processing, onEdit, onRemove]
    )

    return (
      <StyledImagesContainerList>
        {Array.isArray(images) && images.length > 0
          ? images.map(renderImage)
          : null}
        <div
          className="open-dialog"
          onClick={() => {
            document.getElementById("dropzoneInput").click()
          }}>
          <FontAwesomeIcon icon={faPlus} size="3x" />
        </div>
      </StyledImagesContainerList>
    )
  }
)

const ImageGallery = ({ onDeleteImage, processingImages = [] }) => {
  console.log("ImageGallery mounted")
  const [croppingImage, setCroppingImage] = useState(null)
  const { error: errorToast } = useToast()
  const { formatMessage } = useIntl()

  const { values, setFieldValue } = useFormikContext()
  console.log("Formik values:", values)

  // Ensure images is always an array and memoize it
  const images = useMemo(() => {
    return Array.isArray(values?.images) ? values.images : []
  }, [values?.images])
  console.log("Processed images array:", images)

  const handleDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      console.log("handleDrop called:", { acceptedFiles, fileRejections })
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === "file-too-large") {
            errorToast(
              formatMessage({
                id: "dashboard::forms::imageTooLarge",
                defaultMessage: "The file size must be smaller than 3MB",
              })
            )
          }

          if (err.code === "file-invalid-type") {
            errorToast(
              formatMessage({
                id: "dashboard::forms::invalidFileType",
                defaultMessage: "Invalid file type",
              })
            )
          }
        })
      })
      Promise.all(
        acceptedFiles.map((file, index) => {
          const id = `user-upload-${uuidv4()}-${Date.now()}`
          return {
            id,
            image: file,
            order: index,
          }
        })
      ).then((results) => {
        console.log("Adding new images:", results)
        setFieldValue("images", [...images, ...results])
      })
    },
    [images, setFieldValue, errorToast, formatMessage]
  )

  const handleRemoveImage = useCallback(
    (id) => {
      console.log("handleRemoveImage called:", { id })
      id.indexOf("user-upload") === -1
        ? onDeleteImage({
            variables: { input: { id } },
          })
            .then(({ data, errors }) => {
              if (errors?.length) {
                throw new Error(errors[0].message)
              } else if (data) {
                setFieldValue("images", [
                  ...images.filter((img) => img.id !== id),
                ])
              }
            })
            .catch((err) => {
              errorToast(err?.[0]?.message || err?.message || err)
            })
        : setFieldValue("images", [...images.filter((img) => img.id !== id)])
    },
    [images, setFieldValue, onDeleteImage, errorToast]
  )

  const handleEditImage = useCallback(
    (id) => {
      console.log("handleEditImage called:", { id })
      const imageToCrop = images.find((image) => image.id === id)
      if (!imageToCrop?.image) {
        console.warn("No image found for id:", id)
        return
      }
      console.log("Image to crop:", {
        id: imageToCrop.id,
        imageType: typeof imageToCrop.image,
        isFile: imageToCrop.image instanceof File,
        isBlob: imageToCrop.image instanceof Blob,
        isString: typeof imageToCrop.image === "string",
      })
      setCroppingImage(imageToCrop)
    },
    [images]
  )

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      console.log("ImageGallery unmounting")
      setCroppingImage(null)
    }
  }, [])

  return (
    <>
      <Dropzone
        onDrop={handleDrop}
        accept={"image/*"}
        isEmpty={images.length === 0}>
        <ImageList
          images={images}
          onRemove={handleRemoveImage}
          onEdit={handleEditImage}
          processing={processingImages}
        />
      </Dropzone>
      {croppingImage && (
        <ImageCropper
          isOpen={true}
          image={croppingImage.image}
          id={croppingImage.id}
          onConfirm={({ result, id }) => {
            console.log("ImageCropper onConfirm:", { result, id })
            if (!result || !id) {
              console.warn("Invalid cropper result:", { result, id })
              return
            }
            const newImages = images.map((image) =>
              image.id === id ? { ...image, image: result } : image
            )
            setFieldValue("images", newImages)
            setCroppingImage(null)
          }}
          onCancel={() => {
            console.log("ImageCropper onCancel")
            setCroppingImage(null)
          }}
        />
      )}
    </>
  )
}

export default ImageGallery
